// Auto-generated lightweight class for Part
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { PartReadAPI, PartWriteAPI, PartReadAPISchema, PartWriteAPISchema } from '../types/part';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, HasAttribute, HasAttributeValue, HasDerivedValue, HasInterfacePort, HasRequirement, Mentions, OwnedByUser, PartBindsInterfacePort, RFLPAllocation } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { Attribute } from './attribute';
import { AttributeValue } from './attributevalue';
import { DerivedValue } from './derivedvalue';
import { Expression } from './expression';
import { LogicalPart } from './logicalpart';
import { User } from '../../base/api/user';
import { Requirement } from './requirement';
import { InterfacePort } from './interfaceport';



export interface Part extends Partial<PartReadAPI> {}
export class Part extends NeoNodeBase<PartReadAPI, PartWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "Part";
  static readonly readSchema = PartReadAPISchema;
  static readonly writeSchema = PartWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    attributes: { target: "Attribute", cardinality: "many", isParent: false, relClass: "HasAttribute", direction: "out" },
    attribute_values: { target: "AttributeValue", cardinality: "many", isParent: false, relClass: "HasAttributeValue", direction: "out" },
    derived_values: { target: "DerivedValue", cardinality: "many", isParent: false, relClass: "HasDerivedValue", direction: "out" },
    mentioned_in_expressions: { target: "Expression", cardinality: "many", isParent: false, relClass: "Mentions", direction: "in" },
    allocation_sources: { target: "LogicalPart", cardinality: "many", isParent: false, relClass: "RFLPAllocation", direction: "in" },
    responsible_user: { target: "User", cardinality: "one", isParent: false, relClass: "OwnedByUser", direction: "out" },
    requirements: { target: "Requirement", cardinality: "many", isParent: false, relClass: "HasRequirement", direction: "out" },
    interface_ports: { target: "InterfacePort", cardinality: "many", isParent: false, relClass: "HasInterfacePort", direction: "out" },
    bound_interface_ports: { target: "InterfacePort", cardinality: "many", isParent: false, relClass: "PartBindsInterfacePort", direction: "out" },
  };

  protected get modelName(): string {
    return 'Part';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return Part.label;
  }


  constructor(data: Partial<PartWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: PartReadAPI, loadContext?: Record<string, any>): Part {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { requirements, interface_ports, bound_interface_ports, security_scope, attributes, attribute_values, derived_values, mentioned_in_expressions, allocation_sources, responsible_user, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = PartReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, requirements: requirements, interface_ports: interface_ports, bound_interface_ports: bound_interface_ports, security_scope: security_scope, attributes: attributes, attribute_values: attribute_values, derived_values: derived_values, mentioned_in_expressions: mentioned_in_expressions, allocation_sources: allocation_sources, responsible_user: responsible_user };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return Part.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'requirements', 'Requirement', ModelRegistry);
    loadManyRelationship(processed, 'interface_ports', 'InterfacePort', ModelRegistry);
    loadManyRelationship(processed, 'bound_interface_ports', 'InterfacePort', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadManyRelationship(processed, 'attributes', 'Attribute', ModelRegistry);
    loadManyRelationship(processed, 'attribute_values', 'AttributeValue', ModelRegistry);
    loadManyRelationship(processed, 'derived_values', 'DerivedValue', ModelRegistry);
    loadManyRelationship(processed, 'mentioned_in_expressions', 'Expression', ModelRegistry);
    loadManyRelationship(processed, 'allocation_sources', 'LogicalPart', ModelRegistry);
    loadOneRelationship(processed, 'responsible_user', 'User', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new Part(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'attributes' - Python ORM style (cached)
  get attributes(): RelationshipManager<this, Attribute> {
    return this.getOrCreateRelationshipManager('attributes', () => {
      return new RelationshipManager(this, 'attributes', 'many', HasAttribute as typeof Relationship);
    });
  }

  // Relationship manager for 'attribute_values' - Python ORM style (cached)
  get attribute_values(): RelationshipManager<this, AttributeValue> {
    return this.getOrCreateRelationshipManager('attribute_values', () => {
      return new RelationshipManager(this, 'attribute_values', 'many', HasAttributeValue as typeof Relationship);
    });
  }

  // Relationship manager for 'derived_values' - Python ORM style (cached)
  get derived_values(): RelationshipManager<this, DerivedValue> {
    return this.getOrCreateRelationshipManager('derived_values', () => {
      return new RelationshipManager(this, 'derived_values', 'many', HasDerivedValue as typeof Relationship);
    });
  }

  // Relationship manager for 'mentioned_in_expressions' - Python ORM style (cached)
  get mentioned_in_expressions(): RelationshipManager<this, Expression> {
    return this.getOrCreateRelationshipManager('mentioned_in_expressions', () => {
      return new RelationshipManager(this, 'mentioned_in_expressions', 'many', Mentions as typeof Relationship);
    });
  }

  // Relationship manager for 'allocation_sources' - Python ORM style (cached)
  get allocation_sources(): RelationshipManager<this, LogicalPart> {
    return this.getOrCreateRelationshipManager('allocation_sources', () => {
      return new RelationshipManager(this, 'allocation_sources', 'many', RFLPAllocation as typeof Relationship);
    });
  }

  // Relationship manager for 'responsible_user' - Python ORM style (cached)
  get responsible_user(): RelationshipManager<this, User> {
    return this.getOrCreateRelationshipManager('responsible_user', () => {
      return new RelationshipManager(this, 'responsible_user', 'one', OwnedByUser as typeof Relationship);
    });
  }

  // Relationship manager for 'requirements' - Python ORM style (cached)
  get requirements(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('requirements', () => {
      return new RelationshipManager(this, 'requirements', 'many', HasRequirement as typeof Relationship);
    });
  }

  // Relationship manager for 'interface_ports' - Python ORM style (cached)
  get interface_ports(): RelationshipManager<this, InterfacePort> {
    return this.getOrCreateRelationshipManager('interface_ports', () => {
      return new RelationshipManager(this, 'interface_ports', 'many', HasInterfacePort as typeof Relationship);
    });
  }

  // Relationship manager for 'bound_interface_ports' - Python ORM style (cached)
  get bound_interface_ports(): RelationshipManager<this, InterfacePort> {
    return this.getOrCreateRelationshipManager('bound_interface_ports', () => {
      return new RelationshipManager(this, 'bound_interface_ports', 'many', PartBindsInterfacePort as typeof Relationship);
    });
  }

}
